import { useEffect } from 'react';
import MapView from '@arcgis/core/views/MapView';
import { isValidLatLng } from '../../location/helpers';
import { useOpenSensorGridPanel } from '../../sensors/use-open-sensor-grid-panel';
import { useSensorsEnabled } from '../../sensors/use-sensors-enabled';

/** Left button. Esri reports the DOM button number on its click event. */
const LEFT_MOUSE_BUTTON = 0;

/**
 * Cmd+click (ctrl+click off macOS) on the map opens the sensor grid for that point.
 *
 * A separate listener rather than a branch inside `useAddLayerClickHandler`: that hook owns a
 * large dispatch over every layer's hit test, and a modifier-key shortcut has nothing to do with
 * which graphic was hit. Esri supports multiple click handlers on a view, so this composes.
 *
 * One consequence worth knowing: cmd+clicking directly on a camera icon also runs the layer
 * handler, so it opens that camera's panel as well as the grid. Suppressing that would mean
 * reaching into the other hook's hit test, which is not worth the coupling for a modifier click.
 */
export function useSensorGridCmdClick({ mapView }: { mapView: MapView | undefined }): void {
    // Read here rather than taken as a prop: `esri-map` has no feature flag in scope to pass down.
    const sensorsEnabled = useSensorsEnabled();
    const openSensorGridPanel = useOpenSensorGridPanel();

    useEffect(
        function addSensorGridCmdClickListener() {
            if (!mapView || !sensorsEnabled) {
                return;
            }

            const handle = mapView.on('click', (event) => {
                const nativeEvent = event.native as MouseEvent | undefined;
                // `metaKey` is cmd on macOS. `ctrlKey` is the analogue elsewhere, but on macOS
                // ctrl+click *is* a right click, hence the button guard.
                const hasModifier = Boolean(nativeEvent?.metaKey || nativeEvent?.ctrlKey);
                if (!hasModifier || event.button !== LEFT_MOUSE_BUTTON) {
                    return;
                }
                if (!isValidLatLng(event.mapPoint?.latitude, event.mapPoint?.longitude)) {
                    return;
                }
                openSensorGridPanel({
                    latitude: event.mapPoint.latitude,
                    longitude: event.mapPoint.longitude,
                });
            });

            return function removeListener() {
                handle.remove();
            };
        },
        [mapView, sensorsEnabled, openSensorGridPanel]
    );
}
